import React from 'react';
import { ModerationModel } from './useModeration';
import { SEVERITY_CONFIG, CATEGORY_LABELS } from './moderationTypes';

const sevConfig = (s: string) => SEVERITY_CONFIG[s] || SEVERITY_CONFIG.low;

const RiskProfilePanel: React.FC<{ model: ModerationModel }> = ({ model }) => {
  const { riskLoading, riskProfile, riskUserId, formatDateTime, timeAgo } = model;

  return (
    <div className="rp-bento-card">
      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--rp-text-secondary)' }}>User Risk Profile</h3>

      {riskLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin w-6 h-6 border-2 rounded-full mx-auto" style={{ borderColor: 'var(--rp-border)', borderTopColor: 'var(--rp-text-secondary)' }} />
        </div>
      ) : riskProfile && riskUserId ? (
        <div className="space-y-3">
          {/* Risk summary */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Reports', value: riskProfile.content_reports, color: riskProfile.content_reports > 0 ? 'var(--admin-error)' : '#6b7280' },
              { label: 'Pending', value: riskProfile.content_reports_pending, color: riskProfile.content_reports_pending > 0 ? 'var(--admin-warning)' : '#6b7280' },
              { label: 'Chat Reports', value: riskProfile.chat_message_reports, color: riskProfile.chat_message_reports > 0 ? 'var(--admin-warning)' : '#6b7280' },
              { label: 'Chat Bans', value: riskProfile.chat_bans, color: riskProfile.chat_bans > 0 ? 'var(--admin-error)' : '#6b7280' },
              { label: 'No-Shows', value: riskProfile.no_shows, color: riskProfile.no_shows > 0 ? 'var(--admin-warning)' : '#6b7280' },
              { label: 'Blocks', value: riskProfile.blocks_received, color: riskProfile.blocks_received > 0 ? 'var(--admin-error)' : '#6b7280' },
            ].map(s => (
              <div key={s.label} className="text-center p-2 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                <div className="text-lg font-bold" style={{ color: s.color }}>{s.value}</div>
                <div className="text-[10px]" style={{ color: 'var(--rp-text-tertiary)' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Suspension status */}
          {riskProfile.is_suspended && (
            <div className="p-2 rounded-lg text-xs text-center font-bold" style={{ backgroundColor: 'color-mix(in srgb, var(--admin-error) 15%, transparent)', color: 'var(--admin-error)' }}>
              SUSPENDED until {formatDateTime(riskProfile.suspended_until!)}
            </div>
          )}

          {/* Category breakdown */}
          {Object.keys(riskProfile.report_categories).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-tertiary)' }}>Report Categories</h4>
              <div className="flex flex-wrap gap-1">
                {Object.entries(riskProfile.report_categories).map(([cat, cnt]) => (
                  <span key={cat} className="px-2 py-0.5 rounded text-[10px]" style={{ backgroundColor: 'var(--rp-surface-secondary)', color: 'var(--rp-text-secondary)' }}>
                    {CATEGORY_LABELS[cat] || cat}: {cnt}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recent reports */}
          {riskProfile.recent_reports.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-tertiary)' }}>Recent Reports</h4>
              <div className="space-y-1">
                {riskProfile.recent_reports.map(r => (
                  <div key={r.id} className="p-2 rounded-lg text-xs" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="px-1 py-0 rounded text-[9px] font-bold" style={{ backgroundColor: sevConfig(r.severity).bg, color: sevConfig(r.severity).color }}>
                        {sevConfig(r.severity).label}
                      </span>
                      <span style={{ color: 'var(--rp-text)' }}>{CATEGORY_LABELS[r.category] || r.category}</span>
                      <span className="px-1 py-0 rounded text-[9px]" style={{
                        backgroundColor: r.status === 'pending' ? 'rgba(245,158,11,0.2)' : r.status === 'actioned' ? 'rgba(239,68,68,0.2)' : 'rgba(107,114,128,0.2)',
                        color: r.status === 'pending' ? 'var(--admin-warning)' : r.status === 'actioned' ? 'var(--admin-error)' : '#6b7280',
                      }}>{r.status}</span>
                    </div>
                    <p className="line-clamp-1" style={{ color: 'var(--rp-text-tertiary)' }}>{r.reason}</p>
                    <span style={{ color: 'var(--rp-text-tertiary)' }}>{timeAgo(r.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Risk assessment */}
          <div className="p-3 rounded-lg" style={{
            backgroundColor: (riskProfile.content_reports >= 3 || riskProfile.chat_bans >= 2) ? 'color-mix(in srgb, var(--admin-error) 10%, transparent)' :
              (riskProfile.content_reports >= 1 || riskProfile.blocks_received >= 2) ? 'color-mix(in srgb, var(--admin-warning) 10%, transparent)' : 'color-mix(in srgb, var(--admin-success) 10%, transparent)',
            border: `1px solid ${(riskProfile.content_reports >= 3 || riskProfile.chat_bans >= 2) ? 'color-mix(in srgb, var(--admin-error) 20%, transparent)' :
              (riskProfile.content_reports >= 1 || riskProfile.blocks_received >= 2) ? 'color-mix(in srgb, var(--admin-warning) 20%, transparent)' : 'color-mix(in srgb, var(--admin-success) 20%, transparent)'}`,
          }}>
            <div className="text-xs font-bold mb-1" style={{
              color: (riskProfile.content_reports >= 3 || riskProfile.chat_bans >= 2) ? 'var(--admin-error)' :
                (riskProfile.content_reports >= 1 || riskProfile.blocks_received >= 2) ? 'var(--admin-warning)' : 'var(--admin-success)',
            }}>
              Risk Level: {(riskProfile.content_reports >= 3 || riskProfile.chat_bans >= 2) ? 'HIGH' :
                (riskProfile.content_reports >= 1 || riskProfile.blocks_received >= 2) ? 'MODERATE' : 'LOW'}
            </div>
            <p className="text-[10px]" style={{ color: 'var(--rp-text-tertiary)' }}>
              Based on {riskProfile.content_reports} reports, {riskProfile.chat_bans} chat bans, {riskProfile.blocks_received} blocks, {riskProfile.no_shows} no-shows
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Click a reported user to view their risk profile</p>
        </div>
      )}
    </div>
  );
};

export default RiskProfilePanel;
